import { ActionIcon, Loader, Menu, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TbDots, TbFileSpreadsheet, TbFileZip, TbTrash } from 'react-icons/tb'

import { cleanBillingCache, getInvoiceAttachmentZip, getInvoiceCsv, getInvoiceCsvTotals } from '@/api/main/sdk.gen'
import { invalidateQueriesById } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/auth'
import { triggerBlobDownload } from '@/utils/download'
import { errorMessage } from '@/utils/other'
import { isAdministrator } from '@/utils/permissions'

type ExportKind = 'invoiceCsv' | 'invoiceTotalsCsv' | 'attachmentZip'

const fetchExport = async (kind: ExportKind, billingPeriodId: number): Promise<Blob> => {
  const options = { path: { billing_period_id: billingPeriodId }, parseAs: 'blob', throwOnError: true } as const
  const { data } =
    kind === 'invoiceCsv'
      ? await getInvoiceCsv(options)
      : kind === 'invoiceTotalsCsv'
        ? await getInvoiceCsvTotals(options)
        : await getInvoiceAttachmentZip(options)
  return data as Blob
}

const exportFileName = (kind: ExportKind, billingPeriodId: number) =>
  ({
    invoiceCsv: `invoice-${billingPeriodId}.csv`,
    invoiceTotalsCsv: `invoice-totals-${billingPeriodId}.csv`,
    attachmentZip: `invoice-attachment-${billingPeriodId}.zip`,
  })[kind]

export const BillingActionsMenu = ({ billingPeriodId }: { billingPeriodId: number }) => {
  const { t } = useTranslation('billing')
  const queryClient = useQueryClient()
  const isAdmin = isAdministrator(useAuthStore(state => state.userPermissions))

  const exportMutation = useMutation({
    mutationFn: (kind: ExportKind) => fetchExport(kind, billingPeriodId),
    onSuccess: (blob, kind) => triggerBlobDownload(blob, exportFileName(kind, billingPeriodId)),
    onError: error =>
      notifications.show({ title: t('actions.exportFailed'), message: errorMessage(error, ''), color: 'red' }),
  })

  const cleanCacheMutation = useMutation({
    mutationFn: async () => {
      await cleanBillingCache({ throwOnError: true })
    },
    onSuccess: () => {
      // The cache is cleaned server-side for every period, so refresh all billing data the app holds
      // (other periods included), but leave unrelated queries alone.
      invalidateQueriesById(queryClient, ['getBilledAmountHistory', 'getInvoiceDetail', 'getAllBillingPeriods'])
      notifications.show({ message: t('actions.cleanCacheSuccess'), color: 'green' })
    },
    onError: error =>
      notifications.show({
        title: t('actions.cleanCacheFailed'),
        message: errorMessage(error, ''),
        color: 'red',
      }),
  })

  const confirmCleanCache = () =>
    modals.openConfirmModal({
      title: t('actions.cleanCacheConfirmTitle'),
      children: <Text size="sm">{t('actions.cleanCacheConfirmMessage')}</Text>,
      labels: { confirm: t('actions.cleanCache'), cancel: t('actions.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => cleanCacheMutation.mutate(),
    })

  const busy = exportMutation.isPending || cleanCacheMutation.isPending

  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="default" size="lg" aria-label={t('actions.menu')} disabled={busy}>
          {busy ? <Loader size={16} /> : <TbDots size={18} />}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{t('actions.export')}</Menu.Label>
        <Menu.Item leftSection={<TbFileSpreadsheet size={16} />} onClick={() => exportMutation.mutate('invoiceCsv')}>
          {t('actions.invoiceCsv')}
        </Menu.Item>
        <Menu.Item
          leftSection={<TbFileSpreadsheet size={16} />}
          onClick={() => exportMutation.mutate('invoiceTotalsCsv')}
        >
          {t('actions.invoiceTotalsCsv')}
        </Menu.Item>
        <Menu.Item leftSection={<TbFileZip size={16} />} onClick={() => exportMutation.mutate('attachmentZip')}>
          {t('actions.attachmentZip')}
        </Menu.Item>
        {isAdmin && (
          <>
            <Menu.Divider />
            <Menu.Label>{t('actions.admin')}</Menu.Label>
            <Menu.Item color="red" leftSection={<TbTrash size={16} />} onClick={confirmCleanCache}>
              {t('actions.cleanCache')}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

export default BillingActionsMenu
